/*
* Auth: 陈英杰
*/
import { useRef, useEffect, useState } from 'react';
import useTable from './useTable';
import useColumns from './useColumns';
import useThrottle from './useThrottle';

/**
 * 升级版 useCallback，满足绝大部分函数构建场景。解决 useCallback 多次声明函数导致的性能问题
 * warning： 不能替代useCallback 在 缓存初始值的场景
 * @param  {Object} methods 需要被优化的方法集群
 * @return {Object} 新对象
 * @example
  const { handleChange, handleChange2 } = useMethods({
    handleChange() {
      setPage(1);
    },
    handleChange2() {
      this.handleChange();
    }
  })
*/
export function useMethods(methods) {
  const { current } = useRef({
    methods,
    func: undefined,
  });
  current.methods = methods;
  // 只初始化一次
  if (!current.func) {
    const func = Object.create(null);
    Object.keys(methods).forEach((key) => {
      // 包裹 function 转发调用最新的 methods
      func[key] = (...args) => {
        return current.methods[key].call(current.methods, ...args);
      };
    });
    // 返回给使用方的变量
    current.func = func;
  }
  return current.func;
}
/**
 * 自定义hooks-获取上一个State值
 * @param  {any} value 需要被缓存的值
 * @return {any} 缓存值
 * @example
  const [count, setCount] = useState(0);
  const prevCount = usePrevious(count);
  '''
  setCount(count+1);
  console.log(111, count, prevCount)
*/
export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
/**
 * 锁定pc触控板左右滑翻页事件
 * @example
  useOverscrollNone()
*/
export function useOverscrollNone() {
  useEffect(() => {
    // react的钩子执行顺序比较弱智，先执行新载入的didMounted,再执行离开卸载的 willDidUnMounted
    // 只能通过定时器来确保先执行上一个卸载，再执行新一轮的载入
    setTimeout(() => {
      document.getElementsByTagName('body')[0].style.overscrollBehavior = 'none';
    }, 1000)
    return () => {
      document.getElementsByTagName('body')[0].style.overscrollBehavior = 'auto';
    };
  }, []);
}
/**
 * 实时获取可视区宽度/高度
 * @return {object<width, height>} 返回可视区尺寸对象，包含width和height
 * @example
  const size = useClientSize();
  console.log(size); { width: 100, height: 100 }
*/
export function useClientSize() {
  const [size, setSize] = useState({
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight,
  });

  const onResize = () => {
    setSize({
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
    });
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return size;
}

export {
  useTable,
  useColumns,
  useThrottle
}